import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, ArrowLeft } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import CTABand from "@/components/CTABand";
import { useSEO } from "@/hooks/useSEO";
import {
  HERO_SLIDES, MARQUEE, FEATURES, VALUES_SHORT,
  PRODUCT_PREVIEW, PROCESS_STEPS, IMG, wa,
} from "@/lib/site";

function Hero() {
  const [index, setIndex] = useState(0);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 120]);

  const next = useCallback(() => setIndex((i) => (i + 1) % HERO_SLIDES.length), []);
  const prev = () => setIndex((i) => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);

  useEffect(() => {
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next]);

  const slide = HERO_SLIDES[index];

  return (
    <section className="relative h-[92vh] min-h-[620px] w-full overflow-hidden bg-navy" data-testid="hero-slider">
      {/* Background image with parallax */}
      <AnimatePresence mode="sync">
        <motion.div
          key={index}
          className="absolute inset-0"
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
        >
          <motion.img
            style={{ y }}
            src={slide.image}
            alt={slide.title}
            className="h-[115%] w-full object-cover"
          />
        </motion.div>
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/30" />
      <div className="grain absolute inset-0" />

      {/* Content */}
      <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-6 pb-24 md:px-12 md:pb-32">
        <div className="max-w-3xl">
          <motion.p
            key={`ov-${index}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-5 text-xs font-bold uppercase tracking-[0.3em] text-silver"
          >
            HATTORY KONVEKSI
          </motion.p>
          <h1 className="text-5xl font-black leading-[0.95] tracking-tighter text-white md:text-7xl lg:text-8xl">
            <MaskedLines key={`title-${index}`} lines={[slide.title]} lineClass="pb-2" />
          </h1>
          <motion.p
            key={`sub-${index}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-6 max-w-xl text-lg leading-relaxed text-silver md:text-xl"
          >
            {slide.subtitle}
          </motion.p>
          <motion.div
            key={`cta-${index}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
            className="mt-8"
          >
            <a
              href={wa(slide.waMsg)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-btn"
              className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 font-medium text-navy transition-transform duration-300 hover:scale-105 active:scale-95"
            >
              {slide.cta} <ArrowUpRight className="h-5 w-5" />
            </a>
          </motion.div>
        </div>

        {/* Controls */}
        <div className="mt-12 flex items-center gap-4">
          <div className="flex gap-2" data-testid="hero-dots">
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                aria-label={`Slide ${i + 1}`}
                data-testid={`hero-dot-${i}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-10 bg-white" : "w-4 bg-white/40 hover:bg-white/70"}`}
              />
            ))}
          </div>
          <div className="ml-auto flex gap-2">
            <button onClick={prev} aria-label="Sebelumnya" data-testid="hero-prev" className="rounded-full border border-white/30 p-3 text-white transition-colors hover:bg-white hover:text-navy">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button onClick={next} aria-label="Berikutnya" data-testid="hero-next" className="rounded-full border border-white/30 p-3 text-white transition-colors hover:bg-white hover:text-navy">
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useSEO(
    "Konveksi Custom Berbasis Sistem",
    "HATTORY KONVEKSI — konveksi custom modern (kaos, seragam, jersey, jaket) dengan sistem produksi terstruktur dan quality control berlapis. Profesional, cepat, berkualitas."
  );

  return (
    <>
      <Hero />

      {/* Marquee */}
      <div className="border-y border-silver/20 bg-white py-5" data-testid="marquee">
        <Marquee speed={40} gradient={false} autoFill>
          {MARQUEE.map((m, i) => (
            <span key={i} className="mx-8 text-2xl font-medium tracking-tight text-silver md:text-3xl">
              {m} <span className="mx-4 text-navy/30">✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* Intro */}
      <section className="mx-auto max-w-7xl px-6 py-20 md:px-12 md:py-32" data-testid="intro-section">
        <div className="grid items-center gap-12 md:grid-cols-12 md:gap-16">
          <div className="md:col-span-7">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-silver">Tentang HATTORY</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 text-4xl font-black leading-tight tracking-tight text-navy md:text-5xl">
                Konveksi custom modern yang mengutamakan kualitas, kecepatan, dan sistem yang terstruktur.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/tentang"
                data-testid="intro-about-link"
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-silver px-7 py-3.5 font-medium text-navy transition-colors hover:bg-surface"
              >
                Selengkapnya Tentang Kami <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="md:col-span-5">
            <div className="overflow-hidden rounded-3xl shadow-soft">
              <img src={IMG.textureWhite} alt="Tekstur bahan HATTORY" className="h-[360px] w-full object-cover transition-transform duration-700 hover:scale-105" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-7xl px-6 pb-8 md:px-12" data-testid="features-section">
        <div className="grid gap-6 md:grid-cols-3">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.1}>
              <div className="group h-full overflow-hidden rounded-3xl bg-white shadow-soft transition-transform duration-300 hover:-translate-y-1 hover:shadow-lift">
                <div className="h-48 overflow-hidden">
                  <img src={f.image} alt={f.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="p-7">
                  <h3 className="text-2xl font-bold tracking-tight text-navy">{f.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/80">{f.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Values chips */}
      <section className="mx-auto max-w-7xl px-6 py-16 md:px-12 md:py-24" data-testid="values-section">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-silver">Nilai Perusahaan</p>
        </Reveal>
        <div className="mt-6 flex flex-wrap gap-3">
          {VALUES_SHORT.map((v, i) => (
            <Reveal key={v} delay={i * 0.06}>
              <span className="inline-block rounded-full border border-silver/60 px-6 py-3 text-lg font-medium text-navy">
                {v}
              </span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Product preview */}
      <section className="mx-auto max-w-7xl px-6 py-8 md:px-12" data-testid="product-preview-section">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Reveal><p className="text-xs font-bold uppercase tracking-[0.25em] text-silver">Produk & Layanan</p></Reveal>
            <Reveal delay={0.1}><h2 className="mt-4 text-4xl font-black tracking-tight text-navy md:text-5xl">Kategori Unggulan</h2></Reveal>
          </div>
          <Reveal delay={0.15}>
            <Link to="/produk" data-testid="preview-all-products-link" className="inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 font-medium text-white transition-transform duration-300 hover:scale-105">
              Lihat Semua Produk <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6">
          {PRODUCT_PREVIEW.map((p, i) => (
            <Reveal key={p.name} delay={(i % 3) * 0.08}>
              <Link to="/produk" className="group relative block overflow-hidden rounded-3xl" data-testid={`preview-product-${i}`}>
                <img src={p.image} alt={p.name} className="h-56 w-full object-cover transition-transform duration-700 group-hover:scale-110 md:h-72" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/80 to-transparent" />
                <div className="absolute bottom-0 left-0 flex w-full items-center justify-between p-6">
                  <h3 className="text-xl font-bold text-white md:text-2xl">{p.name}</h3>
                  <ArrowUpRight className="h-6 w-6 text-white transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Workflow preview */}
      <section className="mx-auto max-w-7xl px-6 py-20 md:px-12 md:py-28" data-testid="workflow-preview-section">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Reveal><p className="text-xs font-bold uppercase tracking-[0.25em] text-silver">Proses Kerja</p></Reveal>
            <Reveal delay={0.1}><h2 className="mt-4 text-4xl font-black tracking-tight text-navy md:text-5xl">Enam Langkah Terstruktur</h2></Reveal>
          </div>
          <Reveal delay={0.15}>
            <Link to="/proses" data-testid="preview-process-link" className="inline-flex items-center gap-2 rounded-full border border-silver px-7 py-3.5 font-medium text-navy transition-colors hover:bg-surface">
              Pelajari Proses Kami <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          {PROCESS_STEPS.map((s, i) => (
            <Reveal key={s.n} delay={(i % 6) * 0.06}>
              <div className="h-full rounded-2xl border border-silver/30 p-6 transition-colors hover:border-navy">
                <span className="text-3xl font-black text-silver/60">{s.n}</span>
                <h3 className="mt-4 text-lg font-bold text-navy">{s.title}</h3>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTABand />
    </>
  );
}
